import { useEffect, useState } from "react";
import { toDataURL } from "qrcode";
import type { ApiClient, GatewayPairingQr, GatewayProvisioningCapability, GatewayProvisioningRequest, PublicUser, SafeWhatsAppSession } from "../api/client.js";

export function SessionPage(props: {
  api: ApiClient;
  canManage: boolean;
  canBindSelf?: boolean;
  currentUserId?: string | null;
  initialSessions?: readonly SafeWhatsAppSession[];
  initialUsers?: readonly PublicUser[];
  initialRequests?: readonly GatewayProvisioningRequest[];
  initialCapability?: GatewayProvisioningCapability;
}): React.JSX.Element {
  const [sessions, setSessions] = useState<SafeWhatsAppSession[]>([
    ...(props.initialSessions ?? [])
  ]);
  const [users, setUsers] = useState<PublicUser[]>([...(props.initialUsers ?? [])]);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rebindSessionId, setRebindSessionId] = useState<string | null>(null);
  const [binding, setBinding] = useState(false);
  const [qrImages, setQrImages] = useState<Record<string, { payload: string; src: string }>>({});
  const [gatewayQrs, setGatewayQrs] = useState<Record<string, GatewayPairingQr | null>>({});
  const [requests, setRequests] = useState<GatewayProvisioningRequest[]>([...(props.initialRequests ?? [])]);
  const [capability, setCapability] = useState<GatewayProvisioningCapability | null>(props.initialCapability ?? null);
  const [gatewayBusy, setGatewayBusy] = useState<string | null>(null);
  const [recoveryQueuedFor, setRecoveryQueuedFor] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      setSessions(await props.api.listWhatsAppSessions());
      setError(null);
    } catch {
      setError("无法加载 WhatsApp Session。");
    }
  }

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => { void refresh(); }, 5_000);
    return () => { window.clearInterval(interval); };
  }, []);

  useEffect(() => {
    if (recoveryQueuedFor !== null && sessions.some((session) => session.id === recoveryQueuedFor &&
      session.gatewayRuntime?.state === "READY")) setRecoveryQueuedFor(null);
  }, [recoveryQueuedFor, sessions]);

  async function refreshGateway(): Promise<void> {
    if (!props.canManage && props.canBindSelf !== true) return;
    try {
      const nextCapability = await props.api.getGatewayProvisioningCapability();
      setCapability(nextCapability);
      if (props.canManage) {
        setRequests(await props.api.listGatewayProvisioningRequests());
      } else {
        const mine = await Promise.all(sessions.filter((session) => session.status !== "ARCHIVED")
          .map((session) => props.api.getMyGatewayProvisioningRequest(session.id)));
        setRequests(mine.filter((request): request is GatewayProvisioningRequest => request !== null));
      }
    } catch {
      setCapability(null);
    }
  }

  useEffect(() => {
    void refreshGateway();
    const interval = window.setInterval(() => { void refreshGateway(); }, 5_000);
    return () => { window.clearInterval(interval); };
  }, [sessions, props.api, props.canManage, props.canBindSelf]);

  async function gatewayAction(label: string, action: () => Promise<unknown>): Promise<void> {
    if (gatewayBusy !== null) return;
    setGatewayBusy(label);
    setError(null);
    try {
      await action();
      await Promise.all([refresh(), refreshGateway()]);
    } catch (reason) {
      const code = reason !== null && typeof reason === "object" && "code" in reason
        ? String(reason.code) : "REQUEST_FAILED";
      setError(`${label}未完成（${code}）；请刷新核对服务端状态，不要重复操作。`);
    } finally {
      setGatewayBusy(null);
    }
  }

  async function recoverGateway(sessionId: string): Promise<void> {
    if (gatewayBusy !== null || recoveryQueuedFor === sessionId) return;
    setGatewayBusy("核查并恢复网关");
    setError(null);
    try {
      await props.api.requestGatewayRecovery(sessionId);
      setRecoveryQueuedFor(sessionId);
      await refresh();
    } catch (reason) {
      const code = reason !== null && typeof reason === "object" && "code" in reason
        ? String(reason.code) : "REQUEST_FAILED";
      setError(`网关恢复请求未受理（${code}）；请刷新核对服务端状态，不要重复操作。`);
    } finally {
      setGatewayBusy(null);
    }
  }

  useEffect(() => {
    if (!props.canManage) return;
    void props.api.listUsers().then(setUsers).catch(() => {
      // Sessions remain usable when the user directory is temporarily unavailable.
    });
  }, [props.api, props.canManage]);

  useEffect(() => {
    let cancelled = false;
    const pending = sessions.filter((session) => shouldPollGatewayQr(session));
    void Promise.all(pending.map(async (session) => [session.id, await props.api.getGatewayPairingQr(session.id)] as const))
      .then((entries) => {
        if (!cancelled) setGatewayQrs((current) => ({ ...current, ...Object.fromEntries(entries) }));
      }).catch(() => { /* Keep session state visible if the short-lived QR handoff is unavailable. */ });
    return () => { cancelled = true; };
  }, [sessions]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      sessions
        .filter((session) => displayedQr(session, gatewayQrs) !== null)
        .map(async (session) => {
          const qr = displayedQr(session, gatewayQrs)!;
          return [
          session.id,
          { payload: qr.payload, src: await toDataURL(qr.payload, {
            errorCorrectionLevel: "M",
            margin: 1,
            width: 280
          }) }
        ] as const;
        })
    ).then((entries) => {
      if (cancelled) return;
      setQrImages(Object.fromEntries(entries));
    }).catch(() => {
      if (!cancelled) setError("二维码生成失败，请重新开始绑定。");
    });
    return () => { cancelled = true; };
  }, [sessions, gatewayQrs]);

  async function bindSelf(): Promise<void> {
    if (props.currentUserId === undefined || props.currentUserId === null) return;
    if (binding) return;
    setBinding(true);
    try {
      await props.api.bindWhatsAppSession(props.currentUserId);
      await refresh();
    } catch {
      setError("创建绑定失败。");
    } finally {
      setBinding(false);
    }
  }

  async function archive(session: SafeWhatsAppSession): Promise<void> {
    try {
      await props.api.archiveWhatsAppSession(session.id);
      await refresh();
    } catch {
      setError("归档失败。");
    }
  }

  async function rebindSelf(session: SafeWhatsAppSession): Promise<void> {
    if (props.currentUserId === undefined || props.currentUserId === null) return;
    setRebindSessionId(session.id);
    setError(null);
    try {
      await props.api.archiveWhatsAppSession(session.id);
      await props.api.bindWhatsAppSession(props.currentUserId);
      await refresh();
    } catch {
      await refresh();
      setError("重新绑定失败，请刷新页面后重试。");
    } finally {
      setRebindSessionId(null);
    }
  }

  const visibleSessions = props.canBindSelf === true
    ? sessions.filter((session) => session.status !== "ARCHIVED")
    : sessions;
  const connected = visibleSessions.filter(isConnected).sort(byMostRecent);
  const pending = visibleSessions.filter((session) => session.status !== "ARCHIVED" && !isConnected(session)).sort(byMostRecent);
  const archived = props.canManage ? visibleSessions.filter((session) => session.status === "ARCHIVED").sort(byMostRecent) : [];

  function sessionCard(session: SafeWhatsAppSession): React.JSX.Element {
    const owner = users.find((user) => user.id === session.collectorId);
    const qr = displayedQr(session, gatewayQrs);
    const request = requests.find((item) => item.accountId === session.id &&
      ["PENDING", "APPROVED", "PROVISIONED"].includes(item.state)) ?? null;
    return <li className="scs-session-card" key={session.id}>
      <div className="scs-session-card-heading">
        <div>
          <h3>{props.canBindSelf === true ? "我的 WhatsApp" : owner?.email ?? `催收员 ${session.collectorId}`}</h3>
          {props.canManage ? <small>催收员账号 · {session.collectorId}</small> : null}
        </div>
        <span className={`scs-session-status ${isConnected(session) ? "is-connected" : session.status === "ARCHIVED" ? "is-archived" : "is-pending"}`}>
          {statusLabel(session)}
        </span>
      </div>
      <div className="scs-session-card-facts">
        <p><span>WhatsApp 账号</span><strong>{session.accountPhoneE164 ?? "尚未识别"}</strong></p>
        <p><span>连接状态</span><strong>{session.status}</strong></p>
        {session.channelOwner === "GATEWAY" ? <p><span>网关状态</span><strong>{session.gatewayRuntime?.state ?? "尚未报告"}</strong></p> : null}
        {session.channelOwner === "GATEWAY" && session.gatewayJobState ? <p><span>运行任务</span><strong>{session.gatewayJobState}</strong></p> : null}
      </div>
      {props.canManage ? <details className="scs-session-detail"><summary>查看 Session 详情</summary>
        <dl>
          <dt>Session ID</dt><dd>{session.id}</dd>
          <dt>更新时间</dt><dd><time dateTime={session.updatedAt}>{session.updatedAt}</time></dd>
          <dt>最近错误</dt><dd>{session.lastErrorCode ?? "无"}</dd>
          <dt>重启次数</dt><dd>{session.restartCount}</dd>
        </dl>
      </details> : null}
      {session.status !== "ARCHIVED" ? <p className="scs-session-note">{sessionStatusNote(session)}</p> : null}
      {request !== null ? <p className="scs-session-note">独立 Gateway 申请：{request.state}{request.quotaRecoveryRequired ? " · 配额需管理员核对" : ""}</p> : null}
      {props.canBindSelf === true && (session.channelOwner ?? "LEGACY") === "LEGACY" && request === null ? (
        <button disabled={gatewayBusy !== null || capability?.canRequest !== true}
          onClick={() => { void gatewayAction("提交独立 Gateway 申请", () => props.api.requestGatewayProvisioning(session.id)); }} type="button">
          申请独立 Gateway
        </button>
      ) : null}
      {props.canBindSelf === true && request?.state === "PENDING" ? (
        <button disabled={gatewayBusy !== null} onClick={() => { void gatewayAction("撤回申请", () => props.api.cancelGatewayProvisioning(request.id)); }} type="button">撤回申请</button>
      ) : null}
      {props.canManage && request?.state === "PENDING" ? <div className="scs-session-actions">
        <button disabled={gatewayBusy !== null || capability?.canApprove !== true || request.approvalInProgress}
          onClick={() => { void gatewayAction("批准申请", () => props.api.decideGatewayProvisioning(request.id, "APPROVE")); }} type="button">批准</button>
        <button disabled={gatewayBusy !== null || request.approvalInProgress}
          onClick={() => { void gatewayAction("拒绝申请", () => props.api.decideGatewayProvisioning(request.id, "REJECT")); }} type="button">拒绝</button>
      </div> : null}
      {props.canManage && request?.state === "APPROVED" ? (
        <button disabled={gatewayBusy !== null} onClick={() => { void gatewayAction("执行开通", () => props.api.executeGatewayProvisioning(session.id, request.id)); }} type="button">执行已批准的 Gateway 开通</button>
      ) : null}
      {props.canManage && request?.state === "PROVISIONED" && request.gatewayId !== null && (session.channelOwner ?? "LEGACY") === "LEGACY" ? (
        <button disabled={gatewayBusy !== null} onClick={() => { void gatewayAction("准备扫码通道", () => props.api.prepareGatewayCutover(session.id, request.gatewayId!)); }} type="button">准备扫码通道</button>
      ) : null}
      {props.canManage && request?.state === "PROVISIONED" && request.gatewayId !== null && session.channelOwner === "CUTOVER_PENDING" && session.gatewayRuntime?.state === "READY" ? (
        <button disabled={gatewayBusy !== null} onClick={() => { void gatewayAction("切换 Gateway 所有权", () => props.api.finishGatewayCutover(session.id, request.gatewayId!)); }} type="button">确认最终所有权切换</button>
      ) : null}
      {props.canManage && session.channelOwner === "GATEWAY" &&
        !session.gatewayJobState?.startsWith("PAUS") &&
        (session.gatewayRuntime?.state === "STOPPED" || session.gatewayRuntime?.state === "STALE") ? (
        recoveryQueuedFor === session.id ? <p role="status">恢复核查已排队；请等待网关心跳更新，不要重复提交。</p> :
          <button disabled={gatewayBusy !== null} onClick={() => { void recoverGateway(session.id); }} type="button">核查并恢复网关</button>
      ) : null}
      {props.canManage && session.channelOwner === "GATEWAY" && session.gatewayJobState === "READY" &&
        session.gatewayRuntime?.state === "READY" ? <button disabled={gatewayBusy !== null} onClick={() => {
          if (window.confirm("暂停此独立 Gateway？暂停期间无法收发消息，凭证和账号数据会保留，可由管理员恢复。")) {
            void gatewayAction("暂停网关", () => props.api.requestGatewayPause(session.id));
          }
        }} type="button">暂停独立网关</button> : null}
      {props.canManage && session.channelOwner === "GATEWAY" && session.gatewayJobState === "PAUSED" ?
        <button disabled={gatewayBusy !== null} onClick={() => {
          void gatewayAction("恢复已暂停网关", () => props.api.requestGatewayResume(session.id));
        }} type="button">恢复已暂停网关</button> : null}
      {props.canManage && session.channelOwner === "GATEWAY" && session.gatewayJobState === "PAUSED" &&
        session.gatewayId && session.gatewayRuntime?.lastReportedState === "STOPPED" &&
        session.gatewayRuntime.pendingEventCount === 0 ? <button disabled={gatewayBusy !== null} onClick={() => {
          if (window.confirm("归还此 WhatsApp Session 到旧通道，并在后台撤销独立 Gateway、释放其账号存储配额？此操作不能通过“恢复已暂停网关”撤销。")) {
            void gatewayAction("归还旧通道", () => props.api.returnGatewayToLegacy(session.id, session.gatewayId!));
          }
        }} type="button">归还旧通道并撤销独立网关</button> : null}
      {props.canBindSelf === true && canRebind(session.status) && session.channelOwner !== "GATEWAY" ? (
        <button disabled={rebindSessionId !== null} onClick={() => { void rebindSelf(session); }} type="button">
          {rebindSessionId === session.id ? "正在生成二维码…" : "重新绑定 WhatsApp"}
        </button>
      ) : null}
      {(session.status === "QR_READY" || session.channelOwner === "CUTOVER_PENDING") && qr === null ? (
        <p role="status">二维码已过期，正在自动生成新的二维码…</p>
      ) : qr === null ? null : (
        <figure>
          <figcaption>扫码绑定（到期时间：{qr.expiresAt}）</figcaption>
          {qrImages[session.id]?.payload !== qr.payload ? <p>正在生成二维码…</p> : (
            <img alt="WhatsApp 登录二维码" height="280" src={qrImages[session.id]!.src} width="280" />
          )}
        </figure>
      )}
      {session.status !== "ARCHIVED" && session.channelOwner !== "GATEWAY" && (props.canManage || props.canBindSelf === true) ? (
        <button onClick={() => { void archive(session); }} type="button">{props.canManage ? "归档" : "退出绑定"}</button>
      ) : null}
      {session.channelOwner === "GATEWAY" && session.status !== "ARCHIVED" && props.canBindSelf === true ? (
        <p>此账号由独立 Gateway 管理；需管理员先安全停止通道后才能退出绑定。</p>
      ) : null}
    </li>;
  }

  return (
    <main className="scs-session-page">
      <h1>WhatsApp Session</h1>
      {props.canBindSelf === true && visibleSessions.length === 0 ? (
        <section className="scs-session-empty">
          <h2>尚未绑定 WhatsApp</h2>
          <p>先创建申请所需的 Session 记录，再提交独立 Gateway 申请；创建记录不会直接开通 Gateway。</p>
          <button disabled={binding} onClick={() => { void bindSelf(); }} type="button">
            {binding ? "正在准备绑定…" : "绑定我的 WhatsApp"}
          </button>
        </section>
      ) : null}
      {error === null ? null : <p role="alert">{error}</p>}
      {capability !== null && !capability.canApprove ? <p className="scs-session-note" role="status">当前不能批准新的 Gateway：{capability.blockers.join("、") || "开通能力未就绪"}。已批准申请可继续执行。</p> : null}
      {props.canManage ? <>
        <button onClick={() => { void refresh(); }} type="button">刷新 Session 状态</button>
        <section className="scs-session-group"><h2>已连接 <span>{connected.length}</span></h2>
          {connected.length === 0 ? <p>当前没有已确认连接的 Session。</p> : <ul aria-label="已连接 Session">{connected.map(sessionCard)}</ul>}
        </section>
        <section className="scs-session-group"><h2>待处理 <span>{pending.length}</span></h2>
          {pending.length === 0 ? <p>暂无待处理 Session。</p> : <ul aria-label="待处理 Session">{pending.map(sessionCard)}</ul>}
        </section>
        <section className="scs-session-group scs-session-archive">
          <button aria-expanded={showArchived} onClick={() => { setShowArchived((current) => !current); }} type="button">
            已归档（{archived.length}）{showArchived ? "收起" : "查看"}
          </button>
          {showArchived ? <ul aria-label="已归档 Session">{archived.map(sessionCard)}</ul> : null}
        </section>
      </> : <ul aria-label="Session 列表" className="scs-session-list">{visibleSessions.sort(byMostRecent).map(sessionCard)}</ul>}
    </main>
  );
}

function isConnected(session: SafeWhatsAppSession): boolean {
  if (session.gatewayJobState?.startsWith("PAUS")) return false;
  return session.status === "CONNECTED" &&
    (session.channelOwner !== "GATEWAY" || session.gatewayRuntime?.state === "READY");
}

function statusLabel(session: SafeWhatsAppSession): string {
  if (session.status === "ARCHIVED") return "已归档";
  if (session.gatewayJobState === "PAUSED") return "网关已暂停";
  if (session.gatewayJobState?.startsWith("PAUS")) return "网关暂停中";
  if (isConnected(session)) return "已连接";
  if (session.status === "CONNECTED") return "网关未就绪";
  if (session.status === "QR_READY") return "等待扫码";
  if (session.status === "AUTHENTICATING") return "正在验证";
  if (session.status === "CREATED") return "准备中";
  if (session.status === "AUTH_FAILURE") return "验证失败";
  if (session.status === "DISCONNECTED") return "已断开";
  return "不可用";
}

function byMostRecent(left: SafeWhatsAppSession, right: SafeWhatsAppSession): number {
  return right.updatedAt.localeCompare(left.updatedAt) || left.id.localeCompare(right.id);
}

function canRebind(status: SafeWhatsAppSession["status"]): boolean {
  return status === "DISCONNECTED" || status === "AUTH_FAILURE" || status === "BANNED_OR_UNAVAILABLE";
}

function displayedQr(
  session: SafeWhatsAppSession,
  gatewayQrs: Readonly<Record<string, GatewayPairingQr | null>>
): GatewayPairingQr | null {
  if (session.channelOwner === "CUTOVER_PENDING") return gatewayQrs[session.id] ?? null;
  if (session.channelOwner === "GATEWAY") {
    if (!shouldPollGatewayQr(session)) return null;
    return gatewayQrs[session.id] ?? null;
  }
  return session.qr;
}

function shouldPollGatewayQr(session: SafeWhatsAppSession): boolean {
  return session.channelOwner === "CUTOVER_PENDING" ||
    (session.channelOwner === "GATEWAY" && ["QR_READY", "AUTHENTICATING", "AUTH_FAILURE", "DISCONNECTED", "BANNED_OR_UNAVAILABLE"].includes(session.status));
}

function sessionStatusNote(session: SafeWhatsAppSession): string {
  if (session.channelOwner === "GATEWAY" && !isConnected(session)) {
    if (session.gatewayJobState === "PAUSED") return "独立网关已由管理员安全暂停；凭证及数据保留。需管理员恢复后才可继续收发消息。";
    return session.gatewayRuntime?.state === "STOPPED" || session.gatewayRuntime?.state === "STALE"
      ? "独立网关未运行或心跳过期，当前不能发送或重新扫码；请管理员检查目标网关及事件队列，恢复后刷新状态。不要重复创建绑定。"
      : "独立网关尚未确认连接；请等待网关状态和 WhatsApp 连接同时恢复，不要重复创建绑定。";
  }
  switch (session.status) {
    case "QR_READY":
      return "请在 WhatsApp 的“已关联设备”中扫描上方二维码。二维码会自动更新。";
    case "AUTHENTICATING":
      return "已收到扫码认证，正在同步 WhatsApp。完成后状态会自动变为 CONNECTED。";
    case "CONNECTED":
      return "WhatsApp 已连接；新消息会在匹配到客户后进入会话列表。";
    case "ARCHIVED":
      return "这是历史绑定记录，不能继续使用。";
    case "CREATED":
      return "正在准备 WhatsApp 登录二维码，请保持此页面打开。";
    default:
      return "会话当前不可用；可重新生成二维码开始新的绑定。";
  }
}
